import React from 'react';
import { Platform, Text, TouchableOpacity, View } from 'react-native';
import {
  HelperText,
  IconButton,
  RadioButton,
  TextInput,
} from 'react-native-paper';
import { Picker } from '@react-native-picker/picker';
import ProductImagePicker from './ProductImagePicker';
import { iconSize, v } from '../../../../utils/libs';
import {
  PARENT_VARIANT_THUMBNAIL_CAMERA_ID,
  SIZE_OPTIONS,
  UNIT_OF_MASS_OPTIONS,
  type EditableVariant,
  type ProductFormData,
} from '../productFormTypes';
import { useProductFormFieldTheme } from './productFormStyles';

/**
 * Full-presentation product fields extracted verbatim from NewProduct.tsx
 * (US3, T053): image section, mass/unit, price-type selector, and the
 * Additional Information accordion. Both NewProduct and the wizard's
 * More-options expansion render these exact components.
 */

export interface ProductImageSectionProps {
  previewUri: string;
  onUploaded: (b64: string, local?: string) => void;
  onDiscard: () => void;
  onProcessingChange: (processing: boolean) => void;
  /** Web renders a collapsible accordion; native a plain section. */
  expanded: boolean;
  onToggleExpanded: () => void;
}

export const ProductImageSection: React.FC<ProductImageSectionProps> = ({
  previewUri,
  onUploaded,
  onDiscard,
  onProcessingChange,
  expanded,
  onToggleExpanded,
}) => {
  const { theme, styles } = useProductFormFieldTheme();

  if (Platform.OS === 'web') {
    return (
      <>
        <TouchableOpacity
          onPress={onToggleExpanded}
          style={styles.accordionHeader}>
          <Text style={[styles.accordionTitle, { flex: 1 }]}>
            Product Image
          </Text>
          <IconButton
            icon="chevron-down"
            size={iconSize(20)}
            iconColor={theme.colors.text}
            style={[
              styles.accordionChevron,
              {
                transform: [{ rotate: expanded ? '180deg' : '0deg' }],
              },
            ]}
          />
        </TouchableOpacity>
        {expanded ? (
          <ProductImagePicker
            previewUri={previewUri}
            onUploaded={onUploaded}
            onDiscard={onDiscard}
            onProcessingChange={onProcessingChange}
          />
        ) : null}
      </>
    );
  }

  return (
    <>
      <Text style={styles.sectionTitle}>Product Image</Text>
      <ProductImagePicker
        previewUri={previewUri}
        onUploaded={onUploaded}
        onDiscard={onDiscard}
        onProcessingChange={onProcessingChange}
      />
    </>
  );
};

export interface MassAndUnitFieldsProps {
  formData: Pick<ProductFormData, 'mass' | 'unitofmass'>;
  priceType: string;
  onChangeField: (field: string, value: string) => void;
  /** Deterministic visual focus for non-interactive renderers such as Remotion. */
  focusedField?: 'mass' | 'unit';
}

export const MassAndUnitFields: React.FC<MassAndUnitFieldsProps> = ({
  formData,
  priceType,
  onChangeField,
  focusedField,
}) => {
  const {
    theme,
    styles,
    inputOutlineProps,
    webPlaceholderTheme,
    pickerItemColor,
    pickerItemColorSecondary,
  } = useProductFormFieldTheme();

  const unitPicker = (
    <View
      style={[
        styles.pickerContainer,
        focusedField === 'unit'
          ? { borderColor: theme.colors.primary, borderWidth: 2 }
          : null,
      ]}>
      <Picker
        selectedValue={formData.unitofmass}
        onValueChange={value => onChangeField('unitofmass', String(value))}
        dropdownIconColor={theme.colors.primary}
        mode="dropdown"
        style={styles.picker}>
        <Picker.Item
          label="Select Unit of Mass"
          value=""
          color={pickerItemColorSecondary}
        />
        {UNIT_OF_MASS_OPTIONS.map(opt => (
          <Picker.Item
            key={opt.value}
            label={opt.label}
            value={opt.value}
            color={pickerItemColor}
          />
        ))}
      </Picker>
    </View>
  );

  // UI optimization (create forms): for sell-per-item products, mass/volume and
  // unit-of-mass inputs are no longer shown. Users include size in the product
  // name (e.g. "Coke 500ml"). Create payloads send empty strings for both
  // fields — API shape unchanged. Edit screens still show these fields when an
  // existing product already has mass/unit data (legacy support).
  if (priceType === 'item') {
    // Previously rendered Mass/Volume TextInput + unit picker here.
    // Kept commented for easy restore if we revert this UX decision:
    // return (
    //   <>
    //     <TextInput
    //       mode="outlined"
    //       label="Mass/Volume"
    //       placeholder="Mass/Volume i.e 250"
    //       value={formData.mass}
    //       onChangeText={t => onChangeField('mass', t.replace(/[^0-9.]/g, ''))}
    //       keyboardType="numeric"
    //       placeholderTextColor={theme.colors.textSecondary}
    //       {...inputOutlineProps}
    //       outlineColor={
    //         focusedField === 'mass'
    //           ? theme.colors.primary
    //           : inputOutlineProps.outlineColor
    //       }
    //       {...webPlaceholderTheme}
    //       style={styles.input}
    //     />
    //     {unitPicker}
    //   </>
    // );
    return null;
  }

  // Sell-per-unit: unit-of-mass picker only (mass number not collected on create).
  return <View style={{ marginBottom: v(8) }}>{unitPicker}</View>;
};

export interface PriceTypeSelectorProps {
  priceType: string;
  onChangePriceType: (priceType: string) => void;
  disabled?: boolean;
}

export const PriceTypeSelector: React.FC<PriceTypeSelectorProps> = ({
  priceType,
  onChangePriceType,
  disabled = false,
}) => {
  const { theme, styles } = useProductFormFieldTheme();

  const selectPriceType = (next: string) => {
    if (disabled) return;
    onChangePriceType(next);
  };

  return (
    <View>
      <Text style={styles.priceTypePrompt}>
        How will you sell this product?
      </Text>
      <RadioButton.Group onValueChange={selectPriceType} value={priceType}>
        <TouchableOpacity
          style={[styles.priceTypeOption, disabled ? { opacity: 0.55 } : null]}
          activeOpacity={0.7}
          disabled={disabled}
          onPress={() => selectPriceType('item')}>
          <RadioButton
            value="item"
            color={theme.colors.primary}
            disabled={disabled}
          />
          <View style={styles.priceTypeOptionBody}>
            <Text style={styles.priceTypeOptionLabel}>Sell Per Item</Text>
            <Text style={styles.priceTypeOptionHint}>
              Example — 1 bottle.
            </Text>
          </View>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.priceTypeOption, disabled ? { opacity: 0.55 } : null]}
          activeOpacity={0.7}
          disabled={disabled}
          onPress={() => selectPriceType('unit')}>
          <RadioButton
            value="unit"
            color={theme.colors.primary}
            disabled={disabled}
          />
          <View style={styles.priceTypeOptionBody}>
            <Text style={styles.priceTypeOptionLabel}>Sell Per Unit</Text>
            <Text style={styles.priceTypeOptionHint}>
              Example — by weight or volume
            </Text>
          </View>
        </TouchableOpacity>
      </RadioButton.Group>
    </View>
  );
};

export interface AdditionalInformationSectionProps {
  formData: Pick<
    ProductFormData,
    'barcode' | 'size' | 'model' | 'bp_product_image_uri'
  >;
  hasVariants: boolean;
  priceType: string;
  activeVariantRows: EditableVariant[];
  onChangeField: (field: string, value: string) => void;
  expanded: boolean;
  onToggleExpanded: () => void;
  onScanRootBarcode: () => void;
  /** Variant-parent thumbnail wiring (NewProduct behavior). */
  activeVariantCameraId: string | null;
  setActiveVariantCameraId: (id: string | null) => void;
  onProductImageUploaded: (b64: string, local?: string) => void;
  onDiscardProductImage: () => void;
  onImageProcessingChange: (processing: boolean) => void;
}

export const AdditionalInformationSection: React.FC<
  AdditionalInformationSectionProps
> = ({
  formData,
  hasVariants,
  priceType,
  activeVariantRows,
  onChangeField,
  expanded,
  onToggleExpanded,
  onScanRootBarcode,
  activeVariantCameraId,
  setActiveVariantCameraId,
  onProductImageUploaded,
  onDiscardProductImage,
  onImageProcessingChange,
}) => {
  const {
    theme,
    styles,
    inputOutlineProps,
    webPlaceholderTheme,
    pickerItemColor,
    pickerItemColorSecondary,
  } = useProductFormFieldTheme();

  return (
    <View>
      <TouchableOpacity
        onPress={onToggleExpanded}
        style={styles.accordionHeader}
        testID="additional-information-toggle">
        <Text style={[styles.accordionTitle, { flex: 1 }]}>
          Additional Information
        </Text>
        <IconButton
          icon="chevron-down"
          size={iconSize(20)}
          iconColor={theme.colors.text}
          style={[
            styles.accordionChevron,
            {
              transform: [{ rotate: expanded ? '180deg' : '0deg' }],
            },
          ]}
        />
      </TouchableOpacity>

      {expanded ? (
        <View style={{ gap: v(10) }}>
          {hasVariants ? (
            <>
              <Text style={styles.variantParentImageLabel}>
                Product Thumbnail (optional)
              </Text>
              <Text style={styles.variantParentImageHint}>
                Falls back to first variant image if not set
              </Text>
              <ProductImagePicker
                previewUri={formData.bp_product_image_uri}
                cameraActive={
                  activeVariantCameraId === PARENT_VARIANT_THUMBNAIL_CAMERA_ID
                }
                autoOpenWhenEmpty={
                  activeVariantCameraId === PARENT_VARIANT_THUMBNAIL_CAMERA_ID
                }
                onRequestCameraActivation={() =>
                  setActiveVariantCameraId(PARENT_VARIANT_THUMBNAIL_CAMERA_ID)
                }
                onUploaded={(b64: string, local?: string) => {
                  onProductImageUploaded(b64, local);
                  setActiveVariantCameraId(null);
                }}
                onDiscard={() => {
                  onDiscardProductImage();
                  setActiveVariantCameraId(null);
                }}
                onProcessingChange={onImageProcessingChange}
              />
            </>
          ) : (
            <>
              <Text style={styles.variantParentImageLabel}>
                Product Image (optional)
              </Text>
              <ProductImagePicker
                previewUri={formData.bp_product_image_uri}
                onUploaded={onProductImageUploaded}
                onDiscard={onDiscardProductImage}
                onProcessingChange={onImageProcessingChange}
              />
            </>
          )}
          {!hasVariants ? (
            <>
              <TextInput
                mode="outlined"
                label="Barcode (optional)"
                value={formData.barcode}
                onChangeText={t => onChangeField('barcode', t)}
                placeholderTextColor={theme.colors.textSecondary}
                {...inputOutlineProps}
                {...webPlaceholderTheme}
                style={styles.input}
                right={
                  <TextInput.Icon
                    icon="barcode-scan"
                    color={theme.colors.primary}
                    onPress={onScanRootBarcode}
                  />
                }
              />
              {formData.barcode ? (
                <HelperText
                  type={
                    formData.barcode.trim().length >= 8 &&
                    /^[A-Za-z0-9]+$/.test(formData.barcode.trim())
                      ? 'info'
                      : 'error'
                  }
                  visible>
                  {formData.barcode.trim().length >= 8 &&
                  /^[A-Za-z0-9]+$/.test(formData.barcode.trim())
                    ? null
                    : 'Alphanumeric only, at least 8 characters'}
                </HelperText>
              ) : null}
            </>
          ) : (
            <View style={styles.variantBarcodeList}>
              <Text style={styles.variantBarcodeListTitle}>
                Variant Barcodes
              </Text>
              {activeVariantRows.map((variant, index) => {
                const variantDisplayName = String(
                  variant.bp_variant_display_name ||
                    Object.values(variant.bp_variant_attributes || {})
                      .filter(Boolean)
                      .join(' / ') ||
                    `Variant ${index + 1}`,
                );
                return (
                  <Text
                    key={String(variant._id || variant._localId || index)}
                    style={styles.variantBarcodeListItem}>
                    {`${variantDisplayName}: ${String(variant.bp_variant_barcode || 'No barcode')}`}
                  </Text>
                );
              })}
            </View>
          )}

          {!hasVariants && priceType === 'item' ? (
            <View style={styles.pickerContainer}>
              <Picker
                selectedValue={formData.size}
                onValueChange={value => onChangeField('size', String(value))}
                dropdownIconColor={theme.colors.primary}
                mode="dropdown"
                style={styles.picker}>
                <Picker.Item
                  label="Size"
                  value=""
                  color={pickerItemColorSecondary}
                />
                {SIZE_OPTIONS.map(opt => (
                  <Picker.Item
                    key={opt.value}
                    label={opt.label}
                    value={opt.value}
                    color={pickerItemColor}
                  />
                ))}
              </Picker>
            </View>
          ) : null}

          <TextInput
            mode="outlined"
            label="Model | Type | Make | Other"
            value={formData.model}
            onChangeText={t => onChangeField('model', t)}
            placeholderTextColor={theme.colors.textSecondary}
            {...inputOutlineProps}
            {...webPlaceholderTheme}
            style={styles.input}
          />
        </View>
      ) : null}
    </View>
  );
};
